import {Injectable} from '@angular/core';
import {Color, Mesh, MeshBasicMaterial, Object3D, Raycaster, SphereGeometry, Vector2, Vector3} from 'three';
import {SceneService} from '../Scene/scene.service';
import {CameraService} from '../Scene/camera.service';
import {ContainerService} from '../../Utils/container.service';
import {MouseControlsService} from '../Control/mouse-controls.service';
import {Subject} from 'rxjs';
import {RenderService} from '../Scene/render.service';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {TexturePaintService} from './texture-paint.service';
import {CanvasEvent} from '../Control/controls';
import {BrushService} from './brush.service';

// TODO: move to SenseBurn app
@Injectable({
  providedIn: 'root'
})
export class SensorService {

  public readonly placementControl = 'placeSensor';
  public readonly selectionControl = 'selectSensor';

  private _groupName: 'sensorPoints';
  private _sensorPointsGroup = new Object3D();

  private _sensorSize = 1;
  private _selectedSensor = new Subject();
  private _sensorValues: { name: string, color: Color }[];

  constructor(private brushService: BrushService,
              private sceneService: SceneService,
              private cameraService: CameraService,
              private containerService: ContainerService,
              private renderService: RenderService,
              private mouseControlService: MouseControlsService,
              private editorControlService: EditorControlService) {
    this._sensorPointsGroup.name = this._groupName;
    this.registerControls();
  }

  get selectedSensor(): Subject<any> {
    return this._selectedSensor;
  }

  get sensorValues() {
    return this._sensorValues;
  }

  set sensorValues(value) {
    this._sensorValues = value;
  }

  public toggleSensor() {
    if (this.sceneService.scene.getObjectByName('sensorPoints')) {
      this.sceneService.removeGroup('sensorPoints');
    } else {
      this.sceneService.scene.add(this._sensorPointsGroup);
    }
    this.renderService.render();
  }

  // ---------------------------------------------------------------------------------------------------------------------------------------

  private registerControls() {
    this.editorControlService.registerControl(this.placementControl, {
      events: new Map([
        [CanvasEvent.Down, this.placeSensor.bind(this)],
      ])
    });

    const drawControl = this.editorControlService.customControls.get(this.brushService.paintControl);
    this.editorControlService.registerControl(this.selectionControl, {
      events: new Map([
        [CanvasEvent.Down, this.selectSensor.bind(this)],
        [CanvasEvent.Move, drawControl?.events.get(CanvasEvent.Move)],
        [CanvasEvent.Up,   drawControl?.events.get(CanvasEvent.Up)]
      ]),

      pan: false,
      controlMode: false
    });
  }

  private placeSensor(event: MouseEvent) {
    if (event.buttons !== 2 && this.mouseControlService.multitouch === 1 && this.containerService.loadingStatus === 1) {
      this.sensorPlacement(event.offsetX, event.offsetY);
    }
  }

  private selectSensor(event: MouseEvent) {
    if (event.buttons !== 2 && this.mouseControlService.multitouch === 1 && this.containerService.loadingStatus === 1) {
      // Todo check if datapoint was clicked
      // if datapoint was hit change paint color to corresponding property
      // if not start painting
      this.sensorSelection(event.offsetX, event.offsetY);

      // Start drawing
      const drawControl = this.editorControlService.customControls.get(this.brushService.paintControl);
      drawControl.events.get(CanvasEvent.Down)?.(event);
      this.renderService.render();
    }
  }

  private sensorSelection(offsetX: number, offsetY: number): boolean {
    const point = new Vector3(
        (offsetX / this.cameraService.canvasWidth) * 2 - 1,
        -(offsetY / this.cameraService.canvasHeight) * 2 + 1,
        0.1
    );

    const model = this._sensorPointsGroup.children;
    const intersects = this.mouseControlService.getMouseModelIntersections(new Vector2(point.x, point.y), model);
    if (intersects.length > 0) {
      this._selectedSensor.next(intersects[0].object.userData);
      return true;
    }
    return false;
  }

  private sensorPlacement(offsetX: number, offsetY: number) {
    if (this.sceneService.scene.getObjectByName(this._groupName)) {
      this._sensorPointsGroup.children = [];
    } else {
      this.sceneService.scene.add(this._sensorPointsGroup);
    }
    const point = new Vector3(
        (offsetX / this.cameraService.canvasWidth) * 2 - 1,
        -(offsetY / this.cameraService.canvasHeight) * 2 + 1,
        0.1
    );

    const model = <Mesh>this.containerService.renderGroup.children[0];
    const intersects = this.mouseControlService.getMouseModelIntersection(new Vector2(point.x, point.y), model);
    const calcOffset = this._sensorSize / 4;

    if (intersects.length > 0) {
      const p = model.localToWorld(intersects[0].point);
      this.cameraService.camera.updateProjectionMatrix();
      const p_help = [];
      const direction_help = [
        new Vector3(1, 1, 0),
        new Vector3(-1, 1, 0),
        new Vector3(-1, -1, 0),
        new Vector3(1, -1, 0)
      ];

      for (let i = 0; i < direction_help.length; ++i) {
        p_help.push(p.clone().addScaledVector(direction_help[i].clone()
                     .applyQuaternion(this.cameraService.camera.quaternion), calcOffset));
      }

      // TODO normal throw datapoint and get intersection point on model
      const lookAtVector = new Vector3(0, 0, -1);
      lookAtVector.applyQuaternion(this.cameraService.camera.quaternion).negate();
      for (let i = 0; i < 4; i++) {
        const sensorValue = this._sensorValues[i];
        const center_p = p_help[i].clone();
        center_p.add(lookAtVector.clone().multiplyScalar(1));
        const position = this.calculateSensorPosition(center_p, lookAtVector, model);
        if (position instanceof Vector3) {
          if (p.distanceTo(position) - calcOffset < 0.2) {
            this._sensorPointsGroup.add(this.getMarkerSphere(position, sensorValue.color, sensorValue));
          }
        }
      }
      this.renderService.render();
    }
  }

  private calculateSensorPosition(center_p: Vector3, lookAtVector, model): Vector3 {
    center_p.add(lookAtVector.clone().multiplyScalar(1));
    // const corners = [];
    const raycaster = new Raycaster(center_p, lookAtVector.clone().negate());
    const hits = raycaster.intersectObject(model);

    if (hits.length > 0) {
      return hits[0].point;
    }
  }

  private getMarkerSphere(point, color, data): Mesh {
    const sphereGeo = new SphereGeometry();

    const scale = new Vector3(0.07, 0.07, 0.07);
    sphereGeo.scale(scale.x, scale.y, scale.z);


    // set sphere position to clicked point
    const sphere = new Mesh(sphereGeo, new MeshBasicMaterial({color: color}));
    sphere.position.x = point.x;
    sphere.position.y = point.y;
    sphere.position.z = point.z;
    sphere.userData = data;
    return sphere;
  }
}
